/**
 * Where a visit came from (utm_* params of the landing URL), remembered in the
 * browser so later analytics events of the same person can be attributed.
 *
 * Last touch wins. sessionStorage always holds it for the current visit;
 * localStorage keeps it for 30 days unless the visitor rejected cookies.
 */

export interface Attribution {
  source: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  landing?: string;
  at?: string;
}

const STORAGE_KEY = "buw_attribution";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const FIELDS = ["source", "medium", "campaign", "content", "term", "landing", "at"] as const;

/** Keeps only known string fields, trimmed to a sane length. Safe for untrusted input. */
export function sanitizeAttribution(input: unknown): Attribution | null {
  if (!input || typeof input !== "object") return null;
  const raw = input as Record<string, unknown>;
  const clean: Record<string, string> = {};
  for (const field of FIELDS) {
    const value = raw[field];
    if (typeof value === "string" && value.trim()) {
      clean[field] = value.trim().slice(0, 120);
    }
  }
  return clean.source ? (clean as unknown as Attribution) : null;
}

function cookiesRejected(): boolean {
  try {
    return localStorage.getItem("cookieConsent") === "rejected";
  } catch {
    return true;
  }
}

function read(storage: Storage): Attribution | null {
  try {
    const stored = storage.getItem(STORAGE_KEY);
    return stored ? sanitizeAttribution(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
}

/**
 * Stores the attribution found in the current URL, if any.
 * Returns it only when this call is the one that captured a new landing.
 */
export function captureAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = sanitizeAttribution({
      source: params.get("utm_source"),
      medium: params.get("utm_medium"),
      campaign: params.get("utm_campaign"),
      content: params.get("utm_content"),
      term: params.get("utm_term"),
      landing: window.location.pathname,
      at: new Date().toISOString(),
    });
    if (!fromUrl) return null;

    const current = read(sessionStorage);
    const sameLanding =
      current?.source === fromUrl.source &&
      current?.campaign === fromUrl.campaign &&
      current?.landing === fromUrl.landing;
    if (sameLanding) return null;

    const value = JSON.stringify(fromUrl);
    sessionStorage.setItem(STORAGE_KEY, value);
    if (!cookiesRejected()) localStorage.setItem(STORAGE_KEY, value);
    return fromUrl;
  } catch {
    return null;
  }
}

/** Attribution for the current visitor, or null for direct / unknown traffic. */
export function getAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  captureAttribution();
  try {
    const session = read(sessionStorage);
    if (session) return session;
    if (cookiesRejected()) return null;
    const stored = read(localStorage);
    if (!stored) return null;
    const age = Date.now() - new Date(stored.at ?? 0).getTime();
    if (!(age >= 0 && age < MAX_AGE_MS)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}
